import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import {
  filterTanks,
  type TankListFilter,
} from "src/components/pages/Settings/YachtSetUp/TankPlans/tankLimits.utils";
import { type ITankPlan } from "src/components/pages/Settings/YachtSetUp/TankPlans/tankPlans.model";
import { type ApiError } from "src/interfaces/ApiError";

import { useGetApi } from "./useGetApi";

type Options = {
  enabled?: boolean;
};

type TankPlansData = { list: ITankPlan[] };

type TankPlansSnapshot = {
  data: TankPlansData | undefined;
  error: ApiError | undefined;
  isLoading: boolean;
};

const CACHE_TTL_MS = 30_000;

// Shared across all hook instances so a form issues one get-tanks request.
let cachedData: TankPlansData | undefined;
let cachedAt = 0;
let cachedError: ApiError | undefined;
let inFlight: Promise<TankPlansData | undefined> | null = null;
let snapshot: TankPlansSnapshot = {
  data: undefined,
  error: undefined,
  isLoading: false,
};
const listeners = new Set<() => void>();

const emit = () => {
  snapshot = {
    data: cachedData,
    error: cachedError,
    isLoading: inFlight !== null,
  };
  listeners.forEach(l => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;

const loadTankPlans = (
  fetcher: () => Promise<TankPlansData | undefined>,
  force: boolean,
): Promise<TankPlansData | undefined> => {
  if (!force) {
    if (inFlight) return inFlight;
    if (cachedData && Date.now() - cachedAt < CACHE_TTL_MS) {
      return Promise.resolve(cachedData);
    }
  }
  cachedError = undefined;
  const promise: Promise<TankPlansData | undefined> = fetcher()
    .then(result => {
      if (inFlight === promise && result) {
        cachedData = result;
        cachedAt = Date.now();
      }
      return result;
    })
    .finally(() => {
      if (inFlight === promise) {
        inFlight = null;
        emit();
      }
    });
  inFlight = promise;
  emit();
  return promise;
};

/** Test helper: drop the shared get-tanks cache. */
export const __resetTankPlansCache = () => {
  cachedData = undefined;
  cachedAt = 0;
  cachedError = undefined;
  inFlight = null;
  emit();
};

/** Full get-tanks list (no params), shared across instances; filter client-side. */
export const useTankPlans = ({ enabled = true }: Options = {}) => {
  const { refetch: fetchTanks, error: fetchError } = useGetApi<TankPlansData>({
    request: "get-tanks",
    fetchOnInit: false,
    notifyError: false,
  });
  const { data, error, isLoading } = useSyncExternalStore(
    subscribe,
    getSnapshot,
  );

  useEffect(() => {
    if (!fetchError) return;
    cachedError = fetchError;
    emit();
  }, [fetchError]);

  useEffect(() => {
    if (enabled) void loadTankPlans(() => fetchTanks(), false);
  }, [enabled, fetchTanks]);

  const refetch = useCallback(
    () => loadTankPlans(() => fetchTanks(), true),
    [fetchTanks],
  );

  const list: ITankPlan[] = useMemo(
    () => (enabled && Array.isArray(data?.list) ? data.list : []),
    [enabled, data],
  );

  const getTank = useCallback(
    (tankid: string | undefined | null): ITankPlan | undefined => {
      if (!tankid) return undefined;
      return list.find(t => t.tankid === tankid);
    },
    [list],
  );

  const filter = useCallback(
    (f: TankListFilter): ITankPlan[] => filterTanks(list, f),
    [list],
  );

  return {
    tanks: list,
    getTank,
    filter,
    isLoading: enabled ? isLoading : false,
    refetch,
    error: enabled ? error : undefined,
  };
};
